import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { sessionService } from '../../../services/sessionService';

const SessionHistory = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedSession, setExpandedSession] = useState(null);

  useEffect(() => {
    if (user) {
      loadSessionHistory();
    } else {
      setLoading(false);
      setSessions([]);
    }
  }, [user]);

  const loadSessionHistory = async () => {
    setLoading(true);
    setError('');
    
    try {
      const { data, error: fetchError } = await sessionService?.getSessionHistory();
      
      if (fetchError) {
        setError(fetchError?.message || 'Failed to load session history');
        return;
      }
      
      setSessions(data || []);
    } catch (err) {
      setError(err?.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5]?.map((star) => (
          <Icon
            key={star}
            name="Star"
            size={16}
            className={star <= rating ? 'text-warning fill-warning' : 'text-muted-foreground'} 
          />
        ))}
      </div>
    );
  };

  const formatDate = (date) => {
    const dateObj = new Date(date);
    const options = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
    return dateObj?.toLocaleDateString('en-US', options);
  };

  const toggleExpand = (sessionId) => {
    setExpandedSession(expandedSession === sessionId ? null : sessionId);
  };

  const handleBookFollowUp = (helperName) => {
    navigate('/booking-scheduling', { state: { helperName } });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <p className="mt-2 text-gray-600">Loading session history...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="text-center py-8">
          <p className="text-error mb-4">{error}</p>
          <button 
            onClick={loadSessionHistory}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (sessions?.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-6">Session History</h2>
        <div className="text-center py-8">
          <Icon name="History" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No completed sessions yet</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-foreground">Session History</h2>
        <Button
          variant="ghost"
          iconName="Download"
          iconPosition="left"
        >
          Export History
        </Button>
      </div>
      <div className="space-y-3">
        {sessions?.map((session) => (
          <div key={session?.id} className="card-elevated overflow-hidden">
            <div className="p-4 hover:bg-muted/50 transition-colors duration-200">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Image
                    src={session?.helperImage}
                    alt={session?.helperImageAlt}
                    className="w-16 h-16 rounded-lg object-cover" 
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <h3 className="text-base font-semibold text-foreground">{session?.helperName}</h3>
                      <p className="text-sm text-muted-foreground">{session?.helperRole}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      iconName={expandedSession === session?.id ? "ChevronUp" : "ChevronDown"}
                      onClick={() => toggleExpand(session?.id)} 
                    />
                  </div>
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Icon name="Calendar" size={14} />
                      <span>{formatDate(session?.date)}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Icon name="Clock" size={14} />
                      <span>{session?.duration}</span>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <Icon name="Tag" size={14} />
                      <span>{session?.topic}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    {renderStars(session?.rating)}
                    <span className="text-sm text-muted-foreground">({session?.rating}/5)</span>
                  </div>
                  {session?.followUpScheduled && (
                    <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-success/10 text-success w-fit">
                      <Icon name="CheckCircle" size={14} />
                      <span className="text-xs font-medium">Follow-up scheduled for {formatDate(session?.nextSessionDate)}</span>
                    </div>
                  )}
                </div>
              </div>
              {expandedSession === session?.id && (
                <div className="mt-4 pt-4 border-t border-border space-y-4">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Icon name="MessageSquare" size={16} className="text-primary" />
                      Your Feedback
                    </h4>
                    <p className="text-sm text-foreground bg-muted rounded-lg p-3">{session?.feedback}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center gap-2">
                      <Icon name="Lock" size={16} className="text-primary" />
                      Personal Notes (Private)
                    </h4>
                    <div className="text-sm text-foreground bg-muted rounded-lg p-3 whitespace-pre-line">
                      {session?.personalNotes}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 pt-2">
                    {!session?.followUpScheduled && (
                      <Button
                        variant="outline"
                        iconName="Calendar"
                        iconPosition="left"
                        onClick={() => navigate('/booking-scheduling', { state: { helperName: session?.helperName } })}
                      >
                        Book Follow-up
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      iconName="Download"
                      iconPosition="left"
                      onClick={() => {
                        console.log('Download session summary:', session?.id);
                        // In production, this would trigger a PDF download
                      }}
                    >
                      Download Summary
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default SessionHistory;